import { useState } from 'react';
import { ChevronDown, ChevronTop, Pencil } from 'assets/icons';
import { fillText } from 'components/invoiceImport/invoiceImportText';
import Tooltip from 'components/tooltips/Tooltips';
import UseContext from 'hooks/useContext';

/**
 * The card the company panel and the mission detail are both built from.
 *
 * Four cards of the overview, the contracts and the amendments draw the same
 * frame: a title, something to fold it with, and a bordered box of content.
 * They had drawn it separately, which is how the contracts ended up with rows
 * that carried no border and buttons that matched nothing else.
 *
 * What differs between them is what sits under the content, so that is a slot.
 */
export const cardIconButtonClass =
  'border border-gray-300 rounded-md w-8 h-8 flex justify-center items-center cursor-pointer hover:bg-gray-50 transition-colors flex-shrink-0';

const PanelCard = ({
  /** Shown in a rounded square before the title. */
  icon,
  /** The square's background — a status colour on the amendment requests. */
  iconClassName = 'bg-gray-100',
  title,
  /** The design gives a card carrying an icon a bold title; a plain one else. */
  titleClassName = '',
  subtitle,
  /** Anything to show at the right of the header, before the fold control. */
  badge,
  onEdit,
  collapsible = false,
  defaultCollapsed = false,
  /**
   * The overview cards fold from an outlined square, next to the pencil that
   * matches it. The contract cards, which have no pencil, fold from a bare
   * chevron — the design draws no box around it.
   */
  outlinedFoldControl = true,
  /** Extra classes on the content box — a grid, mostly. */
  contentClassName = '',
  /** Under the content: the actions, which are what these cards differ by. */
  footer,
  children,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const toggle = () => setIsCollapsed((previous) => !previous);
  const { language } = UseContext();
  // Many tests draw these cards without the app's context.
  const text = (key, fallback) =>
    fillText(language?.getValue(`panelCard.${key}`, fallback) ?? fallback, {
      title,
    });
  const editLabel = text('edit', 'Modifier {title}');
  const foldLabel = isCollapsed
    ? text('unfold', 'Déplier {title}')
    : text('fold', 'Replier {title}');

  // The pencil and the fold control sit on a header that now folds the card
  // itself: without this the click would run twice and land back where it
  // started.
  const withoutBubbling = (action) => (event) => {
    event.stopPropagation();
    action();
  };

  return (
    <div className={'border border-gray-100 rounded-lg p-1'}>
      <div
        {...(collapsible && {
          role: 'button',
          tabIndex: 0,
          onClick: toggle,
          onKeyDown: (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            // Space would scroll the panel the card sits in.
            event.preventDefault();
            toggle();
          },
        })}
        className={`flex items-center justify-between px-4 py-2 gap-2 rounded-md ${
          collapsible ? 'cursor-pointer hover:bg-gray-50 transition-colors' : ''
        }`}
      >
        <div className={'flex items-center gap-3 min-w-0'}>
          {icon && (
            <div
              className={`w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0 ${iconClassName}`}
            >
              {icon}
            </div>
          )}
          <div className={'min-w-0'}>
            <div className={`truncate text-gray-900 ${titleClassName}`}>
              {title}
            </div>
            {subtitle && (
              <div className={'truncate text-sm text-gray-500'}>{subtitle}</div>
            )}
          </div>
        </div>
        <div className={'flex items-center gap-2 flex-shrink-0'}>
          {badge}
          {onEdit && (
            <Tooltip text={editLabel} placement={'top'}>
              <button
                type={'button'}
                aria-label={editLabel}
                className={cardIconButtonClass}
                onClick={withoutBubbling(onEdit)}
              >
                <Pencil wh={18} color={'#374151'} />
              </button>
            </Tooltip>
          )}
          {collapsible && (
            <Tooltip text={foldLabel} placement={'top'}>
              <button
                type={'button'}
                aria-label={foldLabel}
                className={
                  outlinedFoldControl
                    ? cardIconButtonClass
                    : 'flex justify-center items-center cursor-pointer flex-shrink-0'
                }
                onClick={withoutBubbling(toggle)}
              >
                {isCollapsed ? (
                  <ChevronDown wh={18} color={'#374151'} />
                ) : (
                  <ChevronTop wh={18} color={'#374151'} />
                )}
              </button>
            </Tooltip>
          )}
        </div>
      </div>
      {!isCollapsed && (
        <>
          {children && (
            <div
              className={`border border-gray-100 rounded-lg overflow-hidden ${contentClassName}`}
            >
              {children}
            </div>
          )}
          {footer && <div className={'pt-2'}>{footer}</div>}
        </>
      )}
    </div>
  );
};

export default PanelCard;
